// TarotResultOnePage.jsx
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const GOLD = "#FFD86E";

function InfoBlock({ icon, title, children }) {
  return (
    <section className="mt-5">
      <div className="relative rounded-[22px] p-[1.5px]">
        <div
          className="rounded-[22px] border border-white/60 px-5 sm:px-6 py-5 sm:py-6"
          style={{ background: "rgba(255,255,255,0.02)" }}
        >
          <h3 className="flex items-center gap-2 text-[18px] sm:text-[20px] font-extrabold">
            <span className="inline-flex w-6 h-6 items-center justify-center">
              {icon}
            </span>
            {title}
          </h3>
          <p className="text-[14px] sm:text-[15px] leading-6 mt-2 text-white/90 text-left">
            {children}
          </p>
        </div>
      </div>
    </section>
  );
}

// helper: ดึง segment ปัจจุบัน (present) พร้อม fallback
const normalizeSegment = (seg, base) => ({
  work: seg?.work ?? base,
  money: seg?.money ?? base,
  love: seg?.love ?? base,
  health: seg?.health ?? base,
  advice: seg?.advice ?? base,
});

const getSegment = (card, key = "present") => {
  const base =
    card?.prediction ||
    card?.text ||
    "ยังไม่มีคำทำนายสำหรับช่วงปัจจุบัน (Present)";

  // รับเฉพาะ object (ไม่เอา array/primitive)
  const seg = card?.[key];
  if (seg && typeof seg === "object" && !Array.isArray(seg)) {
    return normalizeSegment(seg, base);
  }
  return normalizeSegment(undefined, base);
};

export default function TarotResultOnePage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  
  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_tarot_one_card_result",
      category: "Tarot",
      label: "view_tarot_one_card_result",
    });
  }, []);

  // รับจากหน้าเลือกไพ่ (state.cards) หรือใช้ตัวอย่าง
  const fallback = [
    {
      id: 64,
      card: "Ace of Wands (ไม้เท้า 1)",
      image: "/images/tarot/aceofwands.jpeg",
      text: "ไฟเริ่มต้น พลัง และความกล้าลงมือทำ จุดไฟให้โปรเจกต์หรือความสัมพันธ์ ก้าวแรกสำคัญสุด",
      prediction:
        "โอกาสใหม่และแรงบันดาลใจมาแล้ว จังหวะเหมาะสำหรับเริ่มสิ่งที่คิดไว้",
      tags: ["เริ่มต้น", "พลังงาน", "โอกาส"],
    },
  ];

  const cards =
    Array.isArray(state?.cards) && state.cards.length ? state.cards : fallback;
  const card = cards[0]; // หน้า one-page โฟกัสใบแรกที่เลือก
  const present = getSegment(card, "present");

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white">
      {/* Header: Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
        }}
      >
        <div className="w-full h-16 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate("/horoscope/tarot")}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="px-6 pt-16 pb-16 max-w-[720px] mx-auto">
        {/* รูปไพ่ */}
        <div className="flex justify-center mt-4">
          <div className="rounded-[18px] overflow-hidden shadow-[0_14px_36px_rgba(0,0,0,0.45)]">
            <img
              src={card.image}
              alt={card.card}
              className="w-[180px] sm:w-[200px] h-auto object-cover"
            />
          </div>
        </div>

        {/* ชื่อไพ่ + badge ช่วงเวลา */}
        <div className="text-center mt-6">
          <h1 className="text-[22px] sm:text-[26px] font-extrabold">
            {card.card}
          </h1>
          <div className="mt-3">
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-[12px] border"
              style={{ borderColor: "rgba(255,255,255,.5)" }}
            >
              ช่วงเวลา:{" "}
              <span className="ml-1" style={{ color: GOLD }}>
                ปัจจุบัน (Present)
              </span>
            </span>
          </div>
        </div>

        {/* แท็ก */}
        {/* <div className="flex flex-wrap justify-center gap-2 mt-3">
          {(card.tags ?? ["ความรัก", "ความสำเร็จ"]).map((t, idx) => (
            <span
              key={idx}
              className="px-3 py-1.5 rounded-full text-[13px] border border-white/50 text-white/90"
            >
              {t}
            </span>
          ))}
        </div>*/}

        {/* ความหมายของไพ่ (static meaning) */}
        <InfoBlock
          title="ความหมายของไพ่"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {card.text ||
            "เป็นสัญลักษณ์ของความสำเร็จ ความมั่นคง และความอุ่นใจ พร้อมพาโอกาสดี ๆ เข้ามา"}
        </InfoBlock>

        {/* คำทำนายปัจจุบัน: แยกหัวข้อ */}
        <InfoBlock
          title="งาน"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M4 7h16v12H4z" stroke="white" strokeWidth="1.6" />
              <path d="M9 7V5h6v2" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {present.work}
        </InfoBlock>

        <InfoBlock
          title="การเงิน"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="1.6" />
              <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {present.money}
        </InfoBlock>

        <InfoBlock
          title="ความรัก"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 21s-7-4.35-7-9a4 4 0 0 1 7-2.65A4 4 0 0 1 19 12c0 4.65-7 9-7 9Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {present.love}
        </InfoBlock>

        <InfoBlock
          title="สุขภาพ"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <rect
                x="4"
                y="4"
                width="16"
                height="16"
                rx="3"
                stroke="white"
                strokeWidth="1.6"
              />
              <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {present.health}
        </InfoBlock>

        <InfoBlock
          title="คำแนะนำ"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M9 18h6M10 21h4" stroke="white" strokeWidth="1.6" />
              <path
                d="M12 3a7 7 0 0 1 7 7c0 2.2-1.1 3.8-2.6 5.1-.7.6-1.2 1.2-1.4 1.9h-6c-.2-.7-.7-1.3-1.4-1.9C6.1 13.8 5 12.2 5 10a7 7 0 0 1 7-7Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {present.advice}
        </InfoBlock>
      </main>
    </div>
  );
}
